import React, { useState, useCallback, useRef } from 'react';
import { SaveButton, CREATE, Create, useRefresh, useNotify } from 'react-admin';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import { FormSpy } from 'react-final-form';
import { withStyles } from '@material-ui/core/styles';

import dataProvider from '../../../core/providers/IccUserDataProvider';
import DialogTitleWithCloseButton from '../../../core/components/DialogTitleWithCloseButton';
import SimpleFormWithSave from '../../../core/components/form/SimpleFormWithSave';
import FundSummary from '../../funds/FundSummary';
import { validateFund } from '../../funds/creationSteps/utils';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';

const styles = () => ({
  fullWidth: {
    width: '100%'
  }
});

const FundFamilyFundCreateDialog = props => {
  const refresh = useRefresh();
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();
  const [submitting, setSubmitting] = useState(false);
  let fundFormProps = useRef({});
  const { handleDialogToggle, id, showDialog, classes, ...createProps } = props;

  const setFundFormProps = useCallback(formProps => {
    fundFormProps.current = formProps;
  }, []);

  const handleSaveClick = useCallback(() => {
    // Trigger a submit of our custom quick create/update form
    // This is needed because our modal action buttons are oustide the form
    fundFormProps.current.form.submit();
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    fundFormProps.current.form.reset();
    handleDialogToggle();
  }, [handleDialogToggle, submitting]);

  const handleSubmit = useCallback(
    values => {
      setSubmitting(true);

      const normalizedValues = {
        ...values,
        dataRoom: Boolean(values.dataRoom),
        coInvestment: Boolean(values.coInvestment),
        sma: Boolean(values.sma),
        fundBillingAcknowledgement: Boolean(values.acknowledge)
      };

      // As we want to know when the new fund has been created in order to close the modal, we use the
      // dataProvider directly
      let result = dataProvider(CREATE, 'funds', { data: normalizedValues });
      result
        .then(() => {
          refresh();
          handleDialogToggle();
          notify('ra.notification.created');
        })
        .catch(notifyServerError)
        .finally(() => {
          // Dispatch an action letting react-admin know a API call has ended
          fundFormProps.current.form.reset();
          setSubmitting(false);
        });
    },
    [handleDialogToggle, notify, refresh, notifyServerError]
  );

  return (
    <Dialog
      fullWidth
      open={showDialog}
      aria-label="Create new fund"
      maxWidth="md"
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return null;
        handleClose();
      }}
    >
      <DialogTitleWithCloseButton onClose={handleClose}>
        Create new fund
      </DialogTitleWithCloseButton>
      <DialogContent>
        <Create {...createProps} record={{}} className={classes.fullWidth}>
          <SimpleFormWithSave
            toolbar={null}
            resource="funds"
            // We override the redux-form onSubmit prop to handle the submission ourselves
            onSave={handleSubmit}
            variant="standard"
            margin="normal"
            initialValues={{ fundFamilyId: props.id }}
            validate={validateFund}
          >
            <FormSpy>
              {props => {
                setFundFormProps(props);
                return <FundSummary creation disableFundFamily />;
              }}
            </FormSpy>
          </SimpleFormWithSave>
        </Create>
      </DialogContent>
      <DialogActions>
        <SaveButton
          saving={submitting}
          handleSubmitWithRedirect={handleSaveClick}
          label="Create and Add"
          icon={<></>}
        />
        <Button
          onClick={handleClose}
          variant="outlined"
          color="primary"
          disabled={submitting}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default withStyles(styles)(FundFamilyFundCreateDialog);
